import { create } from "zustand"
import type {
  CompanyExpense,
  CreateExpensePayload,
  UpdateExpensePayload,
} from "@/types/company-expenses"
import {
  fetchCompanyExpenses,
  createCompanyExpense,
  fetchExpenseDetail,
  updateExpense,
  deleteExpense,
} from "@/services/company-expenses"

interface CompanyExpensesState {
  expenses: CompanyExpense[]
  currentExpense: CompanyExpense | null
  isLoading: boolean
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
  error: string | null
  fetch: () => Promise<void>
  create: (payload: CreateExpensePayload) => Promise<boolean>
  fetchDetail: (id: number) => Promise<void>
  update: (id: number, payload: UpdateExpensePayload) => Promise<boolean>
  delete: (id: number) => Promise<boolean>
  clearCurrentExpense: () => void
}

export const useCompanyExpensesStore = create<CompanyExpensesState>((set) => ({
  expenses: [],
  currentExpense: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,

  fetch: async () => {
    set({ isLoading: true, error: null })

    try {
      const response = await fetchCompanyExpenses()

      if (response.status !== 1) {
        throw new Error(response.message || "Failed to fetch expenses")
      }

      set({ expenses: response.data, isLoading: false })
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch expenses"
      set({ error: message, isLoading: false })
    }
  },

  create: async (payload: CreateExpensePayload) => {
    set({ isCreating: true, error: null })

    try {
      const response = await createCompanyExpense(payload)

      if (response.status !== 1) {
        throw new Error(response.message || "Failed to create expense")
      }

      set((state) => ({
        expenses: [response.data, ...state.expenses],
        isCreating: false,
      }))

      return true
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create expense"
      set({ error: message, isCreating: false })
      return false
    }
  },

  fetchDetail: async (id: number) => {
    set({ isLoading: true, error: null, currentExpense: null })

    try {
      const response = await fetchExpenseDetail(id)

      if (response.status !== 1) {
        throw new Error(response.message || "Failed to fetch expense details")
      }

      set({ currentExpense: response.data, isLoading: false })
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch expense details"
      set({ error: message, isLoading: false })
    }
  },

  update: async (id: number, payload: UpdateExpensePayload) => {
    set({ isUpdating: true, error: null })

    try {
      const response = await updateExpense(id, payload)

      if (response.status !== 1) {
        throw new Error(response.message || "Failed to update expense")
      }

      set((state) => ({
        currentExpense: response.data,
        expenses: state.expenses.map((e) =>
          e.id === id ? response.data : e
        ),
        isUpdating: false,
      }))

      return true
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update expense"
      set({ error: message, isUpdating: false })
      return false
    }
  },

  delete: async (id: number) => {
    set({ isDeleting: true, error: null })

    try {
      const response = await deleteExpense(id)

      if (response.status !== 1) {
        throw new Error(response.message || "Failed to delete expense")
      }

      set((state) => ({
        expenses: state.expenses.filter((e) => e.id !== id),
        currentExpense: null,
        isDeleting: false,
      }))

      return true
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to delete expense"
      set({ error: message, isDeleting: false })
      return false
    }
  },

  clearCurrentExpense: () => {
    set({ currentExpense: null, error: null })
  },
}))
